import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useQuery } from "@apollo/client";

import { type AbsoluteRoute, type RouteParams } from "../../core/routes";
import { useQueryParams } from "../../core/routes/hooks";
import { type GetOrganizationsQuery, getOrganizations } from "../../shared/api/queries";
import { SearchParamKeys, useSearchParams } from "../../shared/hooks";
import { type WebLensFolder, type WebLensOrganization } from "../model";
import { mapWebLensOrganizations, resolveEffectiveSelection } from "../utils/webLensOrganizations";

export interface WebLensFolderSelection {
    loading: boolean;
    failed: boolean;
    organizations: WebLensOrganization[];
    /** Organization owning the current route's :orgId, or null while loading / for a foreign id. */
    selectedOrganization: WebLensOrganization | null;
    /** Effective folder: the URL choice when it resolves within the org, else the org's only folder, else null. */
    selectedFolder: WebLensFolder | null;
    selectFolder: (folderId: string) => void;
}

/**
 * Org + Lens Space selection for web-lens submission, following the container-feature
 * convention: the organization is the route's `:orgId` path segment (like /:orgId/lens-folders),
 * and only the Lens Space rides the query string (?lens-folder=), so it survives reloads and
 * carries across the chooser → submit pages. There is deliberately no org selector here — the org
 * is switched via the app-level menu (top right), whose FEATURE_ORG_ROUTE_MAP entry for webLenses
 * routes back to this chooser under the new org (review feedback: a second on-page selector for
 * the same choice read as two choices, synced only one way).
 *
 * Data source is the shared getOrganizations query (orgs with lensSpacesList nested, one call,
 * already fetched app-wide so Apollo answers from cache). Deliberately NOT the orgs-v2
 * childResources stack nor useSelectedOrganizationContainer: both gate on CofConfigId.MyLensesOrgsV2
 * (web-lens creators are allowlisted on the web-lens config only). Folder write authorization is
 * Lens Central's job at submit time — this hook only scopes the picker to the creator's own
 * organizations.
 */
export const useWebLensFolderSelection = (): WebLensFolderSelection => {
    const { data, loading, error } = useQuery<GetOrganizationsQuery>(getOrganizations);
    const { orgId = "" } = useParams<RouteParams[AbsoluteRoute.WEB_LENSES]>();
    const searchParams = useSearchParams();
    const { updateQueryParams } = useQueryParams();

    const requestedFolderId = searchParams.get(SearchParamKeys.LensFolder) ?? "";

    const organizations = useMemo(() => mapWebLensOrganizations(data), [data]);
    const { organization, folder } = useMemo(
        () => resolveEffectiveSelection(organizations, orgId, requestedFolderId),
        [organizations, orgId, requestedFolderId],
    );

    const selectFolder = useCallback(
        (folderId: string) => {
            updateQueryParams({ [SearchParamKeys.LensFolder]: folderId });
        },
        [updateQueryParams],
    );

    return {
        loading,
        failed: Boolean(error),
        organizations,
        selectedOrganization: organization,
        selectedFolder: folder,
        selectFolder,
    };
};
